import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ProjectTodo } from '../ducks';
import {
  createTaskAction,
  moveTodo,
  updateTodoPosition,
} from '../ducks';
import { deleteTodoAction, setError as setTodosError } from '@/features/todos/ducks';
import { selectTodosError } from '@/features/todos/selectors';
import { selectHouseholdUsers } from '@/features/household/selectors';
import {
  RowState,
  classifyPlanned,
  selectChildrenMap,
  selectError,
  selectProjectRowStateByTodo,
  selectTodosById,
  selectTreeRoots,
} from '../selectors';
import { setError } from '../ducks';
import { pickSortOrder } from '../dnd/sortOrder';
import { flattenProjectTree, FlatRow } from '../treeFlatten';
import { TodoRow } from './TodoRow';

interface OutlineProps {
  projectId: string;
}

const INDENT_PX = 24;
const COLLAPSE_STORAGE_KEY = 'projects.outline.collapsed';

export type DragIndent = 'same' | 'nest' | 'unnest';

export interface DragPreview {
  activeId: string;
  overId: string;
  indent: DragIndent;
  // Set when the user is hovering a nest drop that would create a cycle
  // (target is inside the active subtree). The row paints red instead of
  // blue and the drop is dropped silently in handleDragEnd.
  illegal: boolean;
}

function collectSubtree(rootId: string, childrenMap: Record<string, ProjectTodo[]>): Set<string> {
  const out = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    for (const kid of childrenMap[id] || []) {
      if (!out.has(kid.id)) {
        out.add(kid.id);
        stack.push(kid.id);
      }
    }
  }
  return out;
}

function loadCollapsed(projectId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${COLLAPSE_STORAGE_KEY}.${projectId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function saveCollapsed(projectId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(
      `${COLLAPSE_STORAGE_KEY}.${projectId}`,
      JSON.stringify(Array.from(ids)),
    );
  } catch {
    // ignore quota errors
  }
}

export function Outline({ projectId }: OutlineProps) {
  const dispatch = useDispatch();
  const roots = useSelector(selectTreeRoots);
  const childrenMap = useSelector(selectChildrenMap);
  const todosById = useSelector(selectTodosById);
  const rowStateSelector = useMemo(
    () => selectProjectRowStateByTodo(projectId),
    [projectId],
  );
  const rowStateByTodo = useSelector(rowStateSelector);
  // Scheduled-task set, same classifier the inbox uses, so the "Planned" badge
  // shows identically in the editable outline and the read-only inbox.
  const plannedSet = useMemo(
    () => classifyPlanned(Object.values(todosById)),
    [todosById],
  );
  // Default [] so a store without the household slice is safe; the >1 gate
  // hides the avatar in single-member households.
  const outlineUsers = useSelector(selectHouseholdUsers) ?? [];
  const projectsError = useSelector(selectError);
  const todosError = useSelector(selectTodosError);
  // Either the projects saga (move/status) or the todos saga (modal's
  // Complete/Update) can fail; both go here so a 409 from a blocked
  // parent-close is visible whether the click came from the outline
  // glyph or the modal's Complete button.
  const error = projectsError ?? todosError;
  const [addTitle, setAddTitle] = useState('');
  const [filter, setFilter] = useState<'all' | RowState>('all');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(projectId));
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  // Inline subtask creation: when the user clicks the "+" on a row, that
  // row's id goes here and an inline input renders directly under the
  // parent. Submit creates the task and reparents it server-side; Esc /
  // blur clears.
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);
  const [childTitle, setChildTitle] = useState('');

  useEffect(() => {
    setCollapsed(loadCollapsed(projectId));
  }, [projectId]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => {
      dispatch(setError(null));
      dispatch(setTodosError(null));
    }, 6000);
    return () => clearTimeout(t);
  }, [error, dispatch]);

  const toggleCollapsed = useCallback(
    (id: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        saveCollapsed(projectId, next);
        return next;
      });
    },
    [projectId],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const allRows = useMemo<FlatRow[]>(
    () => flattenProjectTree(roots, childrenMap, collapsed),
    [roots, childrenMap, collapsed],
  );

  // Move dispatcher shared by drag and keyboard handlers below. Computes
  // sort_order for inserting movedId at insertIdx in newParentId's
  // children, then fires the optimistic move + server update.
  const dispatchMove = useCallback(
    (movedId: string, newParentId: string | null, insertIdx: number) => {
      const moved = todosById[movedId];
      if (!moved) return;
      const parentKey = newParentId ?? '__root__';
      const newSiblings = (childrenMap[parentKey] || []).filter((t) => t.id !== movedId);
      const sortOrder = pickSortOrder(newSiblings, insertIdx);
      const prevParent = moved.parent_todo_id ?? null;
      const prevSort = moved.sort_order ?? null;
      if (newParentId === prevParent && sortOrder === prevSort) return;
      dispatch(moveTodo({ todoId: movedId, parentId: newParentId, sortOrder }));
      dispatch(
        updateTodoPosition({
          projectId,
          todoId: movedId,
          parentId: newParentId,
          sortOrder,
          prevParentId: prevParent,
          prevSortOrder: prevSort,
        }),
      );
    },
    [dispatch, projectId, todosById, childrenMap],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDragPreview({
      activeId: String(event.active.id),
      overId: String(event.active.id),
      indent: 'same',
      illegal: false,
    });
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { active, over, delta } = event;
      if (!over) {
        setDragPreview((p) =>
          p ? { ...p, overId: String(active.id), indent: 'same', illegal: false } : p,
        );
        return;
      }
      const dx = delta?.x ?? 0;
      const indent: DragIndent = dx > INDENT_PX ? 'nest' : dx < -INDENT_PX ? 'unnest' : 'same';
      const activeId = String(active.id);
      const overId = String(over.id);
      let illegal = false;
      if (indent === 'nest' && activeId !== overId) {
        const subtree = collectSubtree(activeId, childrenMap);
        if (subtree.has(overId)) illegal = true;
      }
      setDragPreview({ activeId, overId, indent, illegal });
    },
    [childrenMap],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over, delta } = event;
      setDragPreview(null);

      if (!over) return;

      const moved = todosById[String(active.id)];
      const target = todosById[String(over.id)];
      if (!moved || !target) return;

      const dx = delta?.x ?? 0;
      const sameRow = active.id === over.id;
      const nest = dx > INDENT_PX;
      const unnest = dx < -INDENT_PX;

      let newParentId: string | null;
      let insertIdx: number;

      if (nest) {
        if (sameRow) return;
        const subtree = collectSubtree(moved.id, childrenMap);
        if (subtree.has(target.id)) return;
        newParentId = target.id;
        const newSiblings = (childrenMap[target.id] || []).filter((t) => t.id !== moved.id);
        insertIdx = newSiblings.length;
      } else if (unnest) {
        if (moved.parent_todo_id == null) return;
        const currentParent = todosById[moved.parent_todo_id];
        if (!currentParent) return;
        newParentId = currentParent.parent_todo_id ?? null;
        const parentKey = newParentId ?? '__root__';
        const newSiblings = (childrenMap[parentKey] || []).filter((t) => t.id !== moved.id);
        const parentIdx = newSiblings.findIndex((t) => t.id === currentParent.id);
        insertIdx = parentIdx >= 0 ? parentIdx + 1 : newSiblings.length;
      } else {
        if (sameRow) return;
        if ((moved.parent_todo_id ?? null) !== (target.parent_todo_id ?? null)) {
          // Cross-parent reorder on equal indent would mean "move into target's
          // parent as sibling of target" — but that's ambiguous with nest, so
          // require an explicit horizontal drag to switch parents.
          return;
        }
        newParentId = moved.parent_todo_id ?? null;
        const parentKey = newParentId ?? '__root__';
        const newSiblings = (childrenMap[parentKey] || []).filter((t) => t.id !== moved.id);
        const targetIdx = newSiblings.findIndex((t) => t.id === target.id);
        if (targetIdx < 0) return;
        const fullList = childrenMap[parentKey] || [];
        const movedIdx = fullList.findIndex((t) => t.id === moved.id);
        const targetFullIdx = fullList.findIndex((t) => t.id === target.id);
        insertIdx = movedIdx < targetFullIdx ? targetIdx + 1 : targetIdx;
      }

      dispatchMove(moved.id, newParentId, insertIdx);
    },
    [dispatchMove, todosById, childrenMap],
  );

  const handleDragCancel = useCallback(() => setDragPreview(null), []);

  const handleAdd = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const t = addTitle.trim();
      if (!t) return;
      dispatch(createTaskAction(projectId, t));
      setAddTitle('');
    },
    [dispatch, projectId, addTitle],
  );

  const handleStartAddChild = useCallback((parentId: string) => {
    setAddingChildOf((prev) => (prev === parentId ? prev : parentId));
    setChildTitle('');
    // Auto-expand the parent so the new input is visible inline.
    setCollapsed((prev) => {
      if (!prev.has(parentId)) return prev;
      const next = new Set(prev);
      next.delete(parentId);
      saveCollapsed(projectId, next);
      return next;
    });
  }, [projectId]);

  const handleSubmitChild = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const t = childTitle.trim();
      if (!t || !addingChildOf) return;
      dispatch(createTaskAction(projectId, t, addingChildOf));
      setChildTitle('');
      setAddingChildOf(null);
    },
    [dispatch, projectId, addingChildOf, childTitle],
  );

  const handleCancelChild = useCallback(() => {
    setAddingChildOf(null);
    setChildTitle('');
  }, []);

  // Delete a row from its inline ✕ affordance. Same action as the
  // Delete/Backspace keyboard path, but subtask-aware: a parent's descendants
  // go with it, so the confirm says so rather than silently taking the subtree.
  const handleDelete = useCallback(
    (todoId: string) => {
      const target = todosById[todoId];
      const descendants = collectSubtree(todoId, childrenMap).size - 1;
      const suffix =
        descendants > 0
          ? ` and its ${descendants} subtask${descendants === 1 ? '' : 's'}`
          : '';
      const ok = window.confirm(
        `Delete "${target?.title ?? 'this task'}"${suffix}? This cannot be undone.`,
      );
      if (!ok) return;
      dispatch(deleteTodoAction(todoId));
    },
    [dispatch, todosById, childrenMap],
  );

  // ── Keyboard shortcuts on focused row.
  //    ⌥↑/⌥↓ — reorder within current parent
  //    Tab/⇧Tab — indent under previous sibling / outdent to grandparent
  //    →/← — expand / collapse a parent row
  //    Enter — open detail (handled by TodoRow itself)
  //    Delete/Backspace — confirm + delete
  //
  //    The outline container catches all keydowns; we early-return when
  //    the event came from an input/textarea (the bottom add field, the
  //    inline subtask field) so users can type freely. Row activation is
  //    detected by document.activeElement having data-todo-id.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Walk to the row owning the focused element so child buttons
      // (chevron / +) still inherit shortcuts.
      const row = target.closest('[data-todo-id]') as HTMLElement | null;
      if (!row) return;
      const todoId = row.getAttribute('data-todo-id');
      if (!todoId) return;
      const moved = todosById[todoId];
      if (!moved) return;
      const parentKey = moved.parent_todo_id ?? '__root__';
      const siblings = childrenMap[parentKey] || [];
      const currentIdx = siblings.findIndex((s) => s.id === todoId);

      // Re-focus the row after a structural change; React rerender drops
      // focus otherwise. data-todo-id makes the row easy to find again.
      const refocus = () => {
        // Defer so the new DOM has rendered.
        requestAnimationFrame(() => {
          const next = document.querySelector(
            `[data-todo-id="${todoId}"]`,
          ) as HTMLElement | null;
          next?.focus();
        });
      };

      // ⌥↑ / ⌥↓ — reorder within parent.
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        if (currentIdx < 0) return;
        const dir = e.key === 'ArrowUp' ? -1 : 1;
        const newIdx = currentIdx + dir;
        if (newIdx < 0 || newIdx >= siblings.length) return;
        // After removing moved from siblings, the target slot is newIdx
        // (which was the position of the swap partner before removal).
        // Walk through: removing currentIdx shifts higher indices down by
        // 1, so when going down (newIdx > currentIdx) we land at newIdx,
        // when going up we land at newIdx (currentIdx shifts to currentIdx-1).
        const insertIdx = dir < 0 ? newIdx : newIdx;
        dispatchMove(todoId, moved.parent_todo_id ?? null, insertIdx);
        refocus();
        return;
      }

      // Tab — indent under previous sibling. ⇧Tab — outdent to grandparent.
      if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) {
          if (moved.parent_todo_id == null) return;
          const currentParent = todosById[moved.parent_todo_id];
          if (!currentParent) return;
          const newParentId = currentParent.parent_todo_id ?? null;
          const newParentKey = newParentId ?? '__root__';
          const newSiblings = (childrenMap[newParentKey] || []).filter(
            (t) => t.id !== todoId,
          );
          const parentIdx = newSiblings.findIndex((t) => t.id === currentParent.id);
          const insertIdx = parentIdx >= 0 ? parentIdx + 1 : newSiblings.length;
          dispatchMove(todoId, newParentId, insertIdx);
          refocus();
        } else {
          if (currentIdx <= 0) return;
          const prevSibling = siblings[currentIdx - 1];
          const newSiblings = childrenMap[prevSibling.id] || [];
          dispatchMove(todoId, prevSibling.id, newSiblings.length);
          // Auto-expand the new parent so the moved row stays visible.
          setCollapsed((prev) => {
            if (!prev.has(prevSibling.id)) return prev;
            const next = new Set(prev);
            next.delete(prevSibling.id);
            saveCollapsed(projectId, next);
            return next;
          });
          refocus();
        }
        return;
      }

      // Arrow keys — expand / collapse parent rows.
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const kids = childrenMap[todoId] ?? [];
        if (kids.length === 0) return;
        const isCollapsed = collapsed.has(todoId);
        if (e.key === 'ArrowRight' && isCollapsed) {
          e.preventDefault();
          toggleCollapsed(todoId);
        } else if (e.key === 'ArrowLeft' && !isCollapsed) {
          e.preventDefault();
          toggleCollapsed(todoId);
        }
        return;
      }

      // Delete / Backspace — confirm + delete the row.
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const ok = window.confirm(`Delete "${moved.title}"? This cannot be undone.`);
        if (!ok) return;
        dispatch(deleteTodoAction(todoId));
        return;
      }
    },
    [
      dispatch,
      projectId,
      todosById,
      childrenMap,
      collapsed,
      toggleCollapsed,
      dispatchMove,
    ],
  );

  const rows = useMemo(
    () =>
      filter === 'all'
        ? allRows
        : allRows.filter((r) => (rowStateByTodo[r.todo.id] ?? 'ready') === filter),
    [allRows, filter, rowStateByTodo],
  );

  const rowIds = useMemo(() => rows.map((r) => r.todo.id), [rows]);

  const counts = { all: allRows.length, ready: 0, blocked: 0, done: 0 };
  for (const r of allRows) {
    const s = rowStateByTodo[r.todo.id] ?? 'ready';
    counts[s] += 1;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        <div className="outline" onKeyDown={handleKeyDown}>
          {error && (
            <div className="outline__error" role="alert">
              <span>{error}</span>
              <button
                type="button"
                className="outline__error-close"
                aria-label="Dismiss error"
                onClick={() => {
                  dispatch(setError(null));
                  dispatch(setTodosError(null));
                }}
              >
                ×
              </button>
            </div>
          )}
          <div className="outline__filters" role="tablist" aria-label="Filter tasks">
            {(['all', 'ready', 'blocked', 'done'] as const).map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                className={`outline__filter${filter === f ? ' outline__filter--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f[0].toUpperCase() + f.slice(1)} <span className="outline__filter-count">{counts[f]}</span>
              </button>
            ))}
          </div>
          {allRows.length === 0 ? (
            <div className="outline__empty">No tasks yet. Add the first one below.</div>
          ) : rows.length === 0 ? (
            <div className="outline__empty">Nothing matches this filter.</div>
          ) : (
            <div className="outline__rows" role="list">
              {rows.map((row) => (
                <React.Fragment key={row.todo.id}>
                  <TodoRow
                    todo={row.todo}
                    depth={row.depth}
                    connectors={row.connectors}
                    hasChildren={row.hasChildren}
                    collapsed={row.collapsed}
                    onToggleCollapsed={toggleCollapsed}
                    onAddChild={handleStartAddChild}
                    onDelete={handleDelete}
                    projectId={projectId}
                    users={outlineUsers}
                    rowState={rowStateByTodo[row.todo.id] ?? 'ready'}
                    planned={plannedSet.has(row.todo.id)}
                    dragPreview={dragPreview}
                  />
                  {addingChildOf === row.todo.id && (
                    <form
                      className="outline__add-child"
                      onSubmit={handleSubmitChild}
                      style={{ paddingLeft: `${(row.depth + 1) * INDENT_PX + 24}px` }}
                    >
                      <input
                        autoFocus
                        className="outline__add-child-input"
                        value={childTitle}
                        onChange={(e) => setChildTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelChild();
                          }
                        }}
                        placeholder={`+ Add subtask of "${row.todo.title}"`}
                        aria-label="New subtask title"
                      />
                      <button
                        type="submit"
                        className="outline__add-submit"
                        disabled={!childTitle.trim()}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="outline__add-cancel"
                        onClick={handleCancelChild}
                        aria-label="Cancel"
                      >
                        ×
                      </button>
                    </form>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          <form className="outline__add" onSubmit={handleAdd}>
            <input
              className="outline__add-input"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              placeholder="+ Add task"
              aria-label="New task title"
            />
            <button type="submit" className="outline__add-submit" disabled={!addTitle.trim()}>
              Add
            </button>
          </form>
        </div>
      </SortableContext>
    </DndContext>
  );
}
